import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { setUser } from "../../../redux/actions";
import { auth } from "../../../firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  reload,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { assignUserRole } from "../../../components/utils/roleManagement";
import AuthHandler from "../../../authHandlers/AuthHandler";
import ButtonSignInProvider from "../../utils/ButtonSignInProvider";
import "../../utils/RegisterPage.css";
import { getFunctions, httpsCallable } from "firebase/functions";
import { onEmailSignUp, onGoogleSignIn } from "./functions/RegisterFunctions";
import LoadingCircle from "../../utils/LoadingCircleSingle";
import StepEnterDetails from "./registrationSteps/StepEnterDetails";
import StepRegisterWithEmail from "./registrationSteps/StepRegisterWithEmail";
import StepVerification from "./registrationSteps/StepVerification";

const functions = getFunctions();

const OnlineRegistrationPageHero = () => {
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [displayedText, setDisplayedText] = useState("");
  const [cursorVisible, setCursorVisible] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showUserExistsScreen, setShowUserExistsScreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showGoogleMessage, setShowGoogleMessage] = useState(false);
  const [showErrorScreen, setShowErrorScreen] = useState(false); // Manage error screen

  const typingPhrase = "<Welcome to Hero/>";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    let typeTimeout;
    let cursorTimeout;

    setDisplayedText("");

    const typeWord = (index) => {
      if (index < typingPhrase.length) {
        setDisplayedText((prev) => prev + typingPhrase[index]);
        typeTimeout = setTimeout(() => typeWord(index + 1), 150);
      } else {
        typeTimeout = setTimeout(() => {
          setDisplayedText("");
          typeWord(0);
        }, 3000);
      }
    };

    typeWord(0);

    const blinkCursor = () => {
      setCursorVisible((prev) => !prev);
      cursorTimeout = setTimeout(blinkCursor, 500);
    };

    blinkCursor();

    return () => {
      clearTimeout(typeTimeout);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const checkIfUserExists = async (userEmail) => {
    const checkUser = httpsCallable(functions, "checkIfUserExists");
    const response = await checkUser({ email: userEmail });
    return response.data;
  };

  const handleContinueToRegister = async () => {
    setError("");
    setLoading(true);

    if (!companyName.trim() || !email.trim()) {
      setError("Both company name and email are required.");
      setLoading(false);
      return;
    }

    try {
      // Check if the user already exists
      const userCheckResponse = await checkIfUserExists(email);
      if (userCheckResponse.exists) {
        setShowUserExistsScreen(true);
        setLoading(false); // Stop loading if user exists
        return;
      }

      // Initialize Cloud Function for duplicate checks
      const checkDuplicate = httpsCallable(functions, "checkDuplicate");

      // Check for duplicate company name
      const companyCheckResponse = await checkDuplicate({
        cp: "heroOrganisations", // Collection path for organisations
        fn: "name", // Field name to check
        fv: companyName, // Field value to check
      });

      if (companyCheckResponse.data.exists) {
        setError(
          "This company name is already in use. Please choose a different name."
        );
        setLoading(false); // Stop loading if duplicate is found
        setShowErrorScreen(true);
        return;
      }

      // If no duplicates, proceed to show the registration form
      setShowRegistrationForm(true);
    } catch (err) {
      setError(
        "An error occurred while checking for duplicates. Please try again."
      );
      console.error("Error in handleContinueToRegister:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAnotherEmail = () => {
    setError("");
    setShowUserExistsScreen(false);
    setShowRegistrationForm(false);
    setShowAdditionalFields(false);
    setEmail("");
    setPassword("");
    setName("");
    setShowGoogleMessage(false);
  };

  const handleEmailSignUp = async (e) => {
    onEmailSignUp({
      e,
      email,
      password,
      name,
      companyName,
      auth,
      setError,
      setSuccess,
      setLoading,
      setCurrentUser, // Correct param
      dispatch,
      setUser, // from redux actions
      setShowErrorScreen, // Error handling
    });
  };

  const handleGoogleSignIn = async () => {
    await onGoogleSignIn({
      auth,
      checkIfUserExists,
      assignUserRole,
      dispatch,
      setUser,
      setError,
      setSuccess,
      setShowGoogleMessage,
      setShowUserExistsScreen,
      companyName,
      navigate,
      setShowErrorScreen,
    });
  };

  const handleResendVerification = async () => {
    if (currentUser || auth.currentUser) {
      try {
        await sendEmailVerification(currentUser || auth.currentUser);
        alert("Verification email resent. Please check your inbox.");
      } catch (error) {
        setError(
          "Failed to resend verification email. Please try again later."
        );
      }
    }
  };

  const handleVerificationCheck = async () => {
    try {
      await reload(currentUser || auth.currentUser);
      if ((currentUser || auth.currentUser).emailVerified) {
        navigate("/registerHeroUserConsole");
      } else {
        alert("User email not verified.");
      }
    } catch (error) {
      setError("An error occurred. Please try again later.");
    }
  };

  return (
    <AuthHandler>
      {({ isThirdStep }) => (
        <div className="hero-register-container">
          <div className="register-left">
            <div className="register-word-container">
              <div className="register-word">
                {displayedText}
                <span
                  className={`register-cursor ${
                    cursorVisible ? "visible" : "hidden"
                  }`}
                >
                  |
                </span>
              </div>
            </div>
          </div>
          <div className="register-right">
            {showErrorScreen ? (
              // Error Screen
              <div className="error-screen">
                <h2 className="error-title">Something Went Wrong</h2>
                <p className="error-message">
                  {error || "An unexpected error occurred. Please try again."}
                </p>
                <button
                  className="register-button"
                  onClick={() => {
                    setShowErrorScreen(false); // Reset the error screen
                    setError(""); // Clear the error message
                    setShowRegistrationForm(false); // Reset the registration form
                    setShowAdditionalFields(false); // Reset additional fields
                    setShowUserExistsScreen(false); // Reset user exists screen
                    setEmail(""); // Reset email field
                    setPassword(""); // Reset password field
                    setName(""); // Reset name field
                    setCompanyName(""); // Reset company name field
                  }}
                >
                  Restart Registration
                </button>
              </div>
            ) : showUserExistsScreen ? (
              // User Exists Screen
              <div className="user-exists-message">
                <h2 className="register-title">User Already Exists</h2>
                <p>Please register with a different email.</p>
                <button
                  className="register-button"
                  onClick={handleTryAnotherEmail}
                >
                  Try Another Email
                </button>
              </div>
            ) : loading ? (
              // Loading Screen
              <LoadingCircle label="Processing..." />
            ) : !showRegistrationForm ? (
              // Step 1: Enter Details
              <StepEnterDetails
                companyName={companyName}
                setCompanyName={setCompanyName}
                email={email}
                setEmail={setEmail}
                error={error}
                handleContinueToRegister={handleContinueToRegister}
              />
            ) : !isThirdStep ? (
              // Step 2: Register with Email
              <StepRegisterWithEmail
                name={name}
                setName={setName}
                password={password}
                setPassword={setPassword}
                error={error}
                handleEmailSignUp={handleEmailSignUp}
                handleGoogleSignIn={handleGoogleSignIn}
              />
            ) : (
              // Step 3: Verification
              <StepVerification
                handleVerificationCheck={handleVerificationCheck}
                handleResendVerification={handleResendVerification}
              />
            )}
          </div>
        </div>
      )}
    </AuthHandler>
  );
};

export default OnlineRegistrationPageHero;
